import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import type { ForumAuthor } from '~/api/forum/types';

import { forumPostsQueryOptions, useForumPosts } from '~/api/forum/useForumPosts';
import { Card, Dialog, LinkButton, Title } from '~/components';
import { headerStyle, listStyle, rootStyle } from '~/pages/Dashboard/ForumPostsSummary/forumPostsSummary.css';
import { ForumAuthorDialog } from '~/pages/Forum/components/ForumAuthorDialog';
import { ForumPostPreview } from '~/pages/Forum/components/ForumPostPreview';
import { ForumPostPreviewSkeleton } from '~/pages/Forum/components/ForumPostPreview.skeleton';
import { routes } from '~/providers/RouterProvider/router.routes';

export const ForumPostsSummary = () => {
  const queryClient = useQueryClient();
  const authorDialogHandle = Dialog.useHandle<ForumAuthor>();

  const { t } = useTranslation(['dashboard']);

  const { data, isError, isPending, isSuccess } = useForumPosts();

  const forumPosts = isSuccess ? data.pages.flatMap(({ data }) => data).slice(0, 3) : [];

  return (
    <Card.Root>
      <section className={rootStyle}>
        <div className={headerStyle}>
          <Title as="h2" styleAs="h3">
            {t('dashboard:forumPostsSummary.title')}
          </Title>

          <LinkButton emphasis="tertiary" icon="ArrowRight" iconPlacement="right" to={routes.forum}>
            {t('dashboard:forumPostsSummary.link')}
          </LinkButton>
        </div>

        {!isError ? (
          <div className={listStyle}>
            {isPending
              ? Array.from({ length: 3 }).map((_, i) => <ForumPostPreviewSkeleton key={i} />)
              : forumPosts.map((post) => <ForumPostPreview authorDialogHandle={authorDialogHandle} key={post.id} post={post} />)}
          </div>
        ) : (
          <Card.Error onRetry={() => queryClient.resetQueries(forumPostsQueryOptions())} title={t('dashboard:forumPostsSummary.error')} />
        )}
      </section>

      <ForumAuthorDialog handle={authorDialogHandle} />
    </Card.Root>
  );
};
